import {h, Fragment} from "preact"
import { SerpElement } from "@nosto/preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { addToCart, getProductBadges, getSrcset, jsonParse, productImagePlaceholder, toggleQuickAdd } from "../helpers";
import { productCarouselOptions, productSizeCarouselOptions } from "../config";
import { fetchColorProducts } from "../api";

import Carousel from "./Carousel";
import NotifyMe from "./elements/NotifyMe";

const colorProductRequests = new Map();

function getInitialSwatches(product) {
  const availableColors = product.customFields?.find((field) => field.key === "product-available_colors")?.value;
  const parsedColors = jsonParse(availableColors || "[]");
  const productColor = product.skus?.[0]?.customFields?.find((field) => field?.key === "color")?.value;
  let selectedSwatch = null;

  const swatches = Array.isArray(parsedColors)
    ? parsedColors.reduce((result, color) => {
        const rawSwatch = Object.values(color || {})[0];
        if (!rawSwatch) return result;
        const swatch = { ...rawSwatch };

        if (productColor === swatch.name) {
          selectedSwatch = { ...swatch, product };
        } else if (swatch.image != null) {
          result.push(swatch);
        }
        return result;
      }, [])
    : [];

  if (selectedSwatch) swatches.unshift(selectedSwatch);
  return { selectedSwatch, swatches };
}

function getColorProductRequest(groupValues) {
  const cacheKey = JSON.stringify(groupValues);
  if (!colorProductRequests.has(cacheKey)) {
    const request = fetchColorProducts(groupValues)
      .then((response) => response?.data?.products?.products || [])
      .catch((error) => {
        colorProductRequests.delete(cacheKey);
        throw error;
      });
    colorProductRequests.set(cacheKey, request);
  }
  return colorProductRequests.get(cacheKey);
}

export default function Product({
  product,
  loading = "auto",
  fetchPriority,
  formatCurrency,
  isDesktop = false,
  pageType,
}) {
  const initialSwatches = useMemo(() => getInitialSwatches(product), [product]);
  const [swatches, setSwatches] = useState(initialSwatches.swatches);
  const [selectedSwatch, setSelectedSwatch] = useState(initialSwatches.selectedSwatch);
  const [prices, setPrices] = useState({
    price: product.price,
    listPrice: product.listPrice,
  });
  const [subFormOpen, setSubFormOpen] = useState(false);
  const [desktopControlsReady, setDesktopControlsReady] = useState(false);
  const subFormRef = useRef(null);

  const customFields = useMemo(
    () => new Map((product.customFields || []).map((field) => [field.key, field.value])),
    [product.customFields],
  );
  const badges = useMemo(() => getProductBadges(product?.tags1), [product?.tags1]);
  const curProductGroup = customFields.get("product-app_color_variants");
  const promoText = customFields.get("product-promotional_copy");
  const comingSoon = product.tags1?.includes("badge:COMING SOON");
  const isFinalSale = product.tags1?.includes("Final Sale");
  const isGiftCard = product.categories?.includes("Gift Cards");
  const activeProduct = selectedSwatch?.product || product;
  const activeUrl = activeProduct.url || product.url;
  const activeName = activeProduct.name || product.name;
  const activeHandle = activeUrl?.split("/products/")[1];
  const activeSkus = selectedSwatch?.product?.skus || product.skus || [];
  const carouselImgs = useMemo(
    () => [activeProduct.imageUrl, ...(activeProduct.alternateImageUrls || [])].filter(Boolean),
    [activeProduct],
  );
  const displayPrice = selectedSwatch?.product?.price || product.price;
  const displayListPrice = selectedSwatch?.product?.listPrice || product.listPrice;
  const currencyCode =
    typeof window !== "undefined"
      ? window.Shopify?.currency?.active || product.priceCurrencyCode
      : product.priceCurrencyCode;
  const formattedPrice = useMemo(
    () => formatCurrency(displayPrice, currencyCode).toString().replace(".00", ""),
    [currencyCode, displayPrice, formatCurrency],
  );
  const formattedListPrice = useMemo(
    () =>
      displayListPrice != null ? formatCurrency(displayListPrice, currencyCode).toString().replace(".00", "") : null,
    [currencyCode, displayListPrice, formatCurrency],
  );

  useEffect(() => {
    const activePriceProduct = selectedSwatch?.product || product;
    setPrices({
      price: activePriceProduct.price,
      listPrice: activePriceProduct.listPrice,
    });
  }, [selectedSwatch]);

  const toggleSubForm = useCallback(() => {
    setSubFormOpen((open) => !open);
  }, []);

  useEffect(() => {
    if (!subFormOpen) return undefined;

    const handleClick = (event) => {
      if (subFormRef.current && !subFormRef.current.contains(event.target)) {
        setSubFormOpen(false);
      }
    };

    window.document.addEventListener("click", handleClick);
    return () => window.document.removeEventListener("click", handleClick);
  }, [subFormOpen]);

  const handleSwatchChange = useCallback(
    (clickedSwatch) => {
      if (clickedSwatch?.product) {
        setSelectedSwatch(clickedSwatch);
      } else {
        const groupValues = jsonParse(curProductGroup || "[]");
        if (!Array.isArray(groupValues) || groupValues.length < 1) return;

        getColorProductRequest(groupValues).then((colorProducts) => {
          let selectedColor = null;
          const updatedSwatches = swatches.map((swatch) => {
            const swatchProduct = colorProducts.find(
              (colorProduct) =>
                swatch.name ===
                colorProduct.skus?.[0]?.attributes?.find((attribute) => attribute?.key === "Color")?.value,
            );
            const updatedSwatch = { ...swatch, product: swatchProduct };
            if (!selectedColor && updatedSwatch.name === clickedSwatch.name) {
              selectedColor = updatedSwatch;
            }
            return updatedSwatch;
          });

          setSwatches(updatedSwatches);
          setSelectedSwatch(selectedColor);
        });
      }

      setTimeout(() => {
        Nosto.migrateToShopifyMarket({
          productSectionElement: `.ns-serp .ns-product-${product.productId}`,
          productUrlElement: ".ns-serp .nosto-product-link",
          productHandleAttribute: "data-handle",
          priceElement: ".ns-serp .nosto-p",
          listPriceElement: ".ns-serp .nosto-lp",
        });
      }, 200);
    },
    [curProductGroup, product.productId, swatches],
  );

  const prepareDesktopControls = useCallback(() => {
    setDesktopControlsReady(true);
  }, []);

  return (
    <SerpElement
      as="article"
      hit={product}
      className={`relative nosto-product-card ns-product-${product.productId}`}
      nosto-id={activeProduct.productId || product.productId}
      data-handle={activeHandle}
    >
      <div
        class="group relative"
        onPointerEnter={isDesktop ? prepareDesktopControls : undefined}
        onFocusCapture={isDesktop ? prepareDesktopControls : undefined}
      >
        <div class="relative">
          <a class="relative group block no-underline mb-2 lg:mb-0 aspect-[2/3] nosto-product-link" href={activeUrl}>
            {isDesktop ? (
              <span class="w-full h-full relative hidden lg:block">
                <img
                  class="image object-cover h-full desktop-image"
                  srcSet={
                    activeProduct.imageUrl ? getSrcset(activeProduct.imageUrl, [320, 440]) : productImagePlaceholder
                  }
                  sizes="(min-width: 768px) 640px, 320px"
                  alt={activeName}
                  title={activeName}
                  loading={loading}
                  fetchpriority={fetchPriority}
                  decoding={fetchPriority === "high" ? "auto" : "async"}
                />
                {carouselImgs[1] && (
                  <img
                    class="image object-cover h-full absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100"
                    srcSet={getSrcset(carouselImgs[1], [1, 440])}
                    sizes="(min-width: 768px) 640px, 0vw"
                    alt={activeName}
                    title={activeName}
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </span>
            ) : carouselImgs.length > 1 ? (
              <Carousel
                options={productCarouselOptions}
                carouselClass="product-images flex lg:hidden"
                wrapperUniqueClass={`images-${product.productId}`}
                init
              >
                <div class="swiper-slide flex justify-center items-center">
                  <img
                    class="w-full h-full object-cover"
                    srcSet={getSrcset(carouselImgs[0], [1, 640])}
                    sizes="(max-width: 1023px) 640px, 1px"
                    alt={activeName}
                    loading={loading}
                    fetchpriority={fetchPriority}
                    decoding={fetchPriority === "high" ? "auto" : "async"}
                  />
                </div>
                <div class="swiper-slide flex justify-center items-center">
                  <img
                    class="w-full h-full object-cover"
                    srcSet={getSrcset(carouselImgs[1], [1, 640])}
                    sizes="(max-width: 1023px) 640px, 1px"
                    alt={activeName}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </Carousel>
            ) : (
              <span class="w-full h-full relative block lg:hidden">
                <img
                  class="w-full h-full object-cover"
                  srcSet={carouselImgs[0] ? getSrcset(carouselImgs[0], [1, 640]) : productImagePlaceholder}
                  sizes="(max-width: 1023px) 640px, 1px"
                  alt={activeName}
                  loading={loading}
                  fetchpriority={fetchPriority}
                  decoding={fetchPriority === "high" ? "auto" : "async"}
                />
              </span>
            )}
          </a>

          <rivo-favorite-button
            data-product-id={activeProduct.productId}
            rivo-ax-data=""
            rivo-ax-cloak=""
            rivo-ax-html="$store.icons.heart"
            rivo-ax-show="$store.rivo_aw_settings?.favorites_enabled &amp;&amp; (!$store.rivo_profile_customer || $store.loading_states.global_favorite_buttons)"
          />

          {!isDesktop && (
            <div class="pointer-events-auto cursor-pointer w-[50px] h-[50px] flex items-end justify-start absolute left-0 bottom-0 pl-3 pb-3 lg:hidden z-1">
              <button
                type="button"
                class="flex justify-center items-center w-7 h-7 rounded-full bg-white"
                onClick={() => {
                  if (isGiftCard) {
                    window.location.href = product.url;
                  } else {
                    toggleQuickAdd(activeHandle, pageType === "category" ? "nosto-product-grid" : "nosto-search");
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path
                    d="M10.625 4.79991L8.66195 1.28213C8.42691 0.860936 7.98234 0.599915 7.5 0.599915V0.599915C7.01766 0.599915 6.57309 0.860936 6.33805 1.28213L4.375 4.79991"
                    stroke="#2D2926"
                  />
                  <path
                    d="M7.5 4.69998C12.184 4.69998 13.9229 6.09118 14.4043 6.59744L14.3916 6.65018L10.6455 14.3972C10.6151 14.46 10.5512 14.4998 10.4814 14.4998H4.51855C4.46643 14.4998 4.41774 14.4777 4.38379 14.4402L4.35449 14.3972L0.608398 6.65018C0.595785 6.6241 0.594311 6.60516 0.594727 6.59744C1.07546 6.09158 2.81448 4.69998 7.5 4.69998Z"
                    stroke="#2D2926"
                  />
                </svg>
              </button>
            </div>
          )}

          {subFormOpen && (
            <NotifyMe product={product} activeVariant={selectedSwatch?.product} subFormRef={subFormRef} />
          )}
        </div>

        {isDesktop && (
          <div class="hidden lg:block opacity-0 h-0 group-hover:h-auto group-hover:opacity-100 group-hover:mt-3.5 transition-all duration-300 ease-in-out">
            {desktopControlsReady && !isGiftCard && activeSkus.length > 0 && (
              <Carousel
                carouselClass="product-sizes"
                wrapperUniqueClass={`sizes-${product.productId}`}
                options={productSizeCarouselOptions}
                init={activeSkus.length > 5}
              >
                {activeSkus.map((sku) => {
                  const value =
                    sku.attributes?.find((attribute) => attribute.key === "Size" || attribute.key === "size")?.value ||
                    sku.customFields?.find((field) => field.key === "Size" || field.key === "size")?.value ||
                    sku.customFields?.find((field) => field.key === "Amount" || field.key === "amount")?.value ||
                    (sku.name?.includes(" / ") ? sku.name.split(" / ")[1] : sku.name);

                  return (
                    <div class="swiper-slide flex justify-center items-center" key={sku.id}>
                      {sku.availability === "InStock" && !comingSoon ? (
                        <button
                          type="button"
                          class="hover:before:w-4 hover:before:absolute hover:before:bottom-0 hover:before:left-1/2 hover:before:-translate-x-1/2 hover:before:h-[1px] hover:before:bg-gray-900 w-full h7--medium relative"
                          data-add-to-cart={pageType === "category" ? "nosto_product_grid" : "nosto_search"}
                          onClick={() => {
                            addToCart(
                              sku.id,
                              pageType === "category" ? "plp" : "search",
                              isFinalSale,
                              activeProduct?.listPrice > activeProduct?.price ? activeProduct.listPrice : null,
                            );
                          }}
                        >
                          {value}
                        </button>
                      ) : (
                        <button
                          type="button"
                          class="h7--medium text-text-disabled font-normal relative after:absolute after:h-[1px] after:bg-text-disabled after:top-1/2 after:-left-1 after:-right-1 after:-translate-y-1/2"
                          onClick={toggleSubForm}
                        >
                          {value}
                        </button>
                      )}
                    </div>
                  );
                })}
              </Carousel>
            )}
          </div>
        )}

        {badges?.length > 0 && (
          <div class="ns-prod-badge flex flex-wrap justify-center gap-x-2 md:gap-2 h8 relative md:absolute md:top-1 md:left-3.5 mx-auto md:mr-3.5">
            {badges.map((badge) => (
              <span
                class="block w-[fit-content] text-left px-1 py-[0.188rem] md:px-2 md:py-0.5 bg-gray-0/80"
                key={badge}
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        <div class="text-center px-2 pb-2.5 lg:pt-2.5">
          <h3 class="h6--medium no-underline text-gray-900">{product.brand}</h3>
          <h4>
            <a class="body-2 no-underline text-gray-900 nosto-prod-name" href={activeUrl}>
              {activeName}
            </a>
          </h4>

          <p class="h6 text-text-subdued nosto-price">
            {prices.listPrice && prices.listPrice > prices.price ? (
              <>
                <span class="visually-hidden sr-only">Original Price:</span>
                <del class="text-gray-600 mr-2 nosto-lp">{formattedListPrice}</del>
                <span class="visually-hidden sr-only">Final Price:</span>
                <span class="text-sale nosto-p">{formattedPrice}</span>
              </>
            ) : (
              <>
                <span class="visually-hidden sr-only">Regular Price:</span>
                <span class="nosto-p">{formattedPrice}</span>
              </>
            )}
          </p>

          {promoText && <h5 class="h7 text-core-3">{promoText}</h5>}
        </div>
      </div>

      {swatches.length > 0 && (
        <div class="pb-2.5">
          <fieldset class="w-full flex gap-3.5 lg:gap-4 h-fit items-center justify-center">
            <legend class="visually-hidden sr-only">Choose a product color:</legend>
            {swatches.slice(0, 5).map((swatch) => (
              <label
                class="swatch rounded-full w-3 h-3 md:w-4 md:h-4 block cursor-pointer"
                title={`Select ${product.name} ${swatch?.name}`}
                style={{
                  backgroundImage: swatch.image ? `url(${swatch.image}&width=50)` : "none",
                }}
                data-swatch={swatch.colorFamily}
                key={swatch.name}
              >
                <input
                  value={swatch.name.toLowerCase().replaceAll(" ", "-")}
                  class="visually-hidden sr-only"
                  type="radio"
                  checked={selectedSwatch?.name === swatch.name}
                  onChange={() => handleSwatchChange(swatch)}
                />
              </label>
            ))}
            {swatches.length > 5 && (
              <a
                class="w-[18px] h-[18px] inline-flex justify-center items-center body-3 leading-none lg:body-2 no-underline"
                href={activeUrl}
              >
                +{swatches.length - 5}
              </a>
            )}
          </fieldset>
        </div>
      )}
    </SerpElement>
  );
}
